/**
 * 统一手机验证组件（C1-C3）
 *
 * voice / callback 共用：分步（手机号 → 6 格 OTP）+ 统一国家选择器 + 统一验证码校验。
 * 自身只负责「采集手机号 → 发码 → 校验」；校验通过后调用 onVerified，由宿主决定后续
 * （voice 拨号、callback 提交线索）。宿主可通过 extraFieldsHtml 注入额外字段（如邮箱/留言）。
 */
import { t } from "../../ui/i18n";
import { getShadowRoot } from "../../ui/container";
import { storage } from "../../core/storage";
import { resolveApiBase } from "../../core/origin";
import type { Locale } from "../../types";

/* ────── 统一国家数据（单一来源，C2） ────── */
export interface Country { code: string; dialCode: string; flag: string; name: string; }
export const COUNTRIES: Country[] = [
  { code: "CN", dialCode: "86", flag: "🇨🇳", name: "中国" },
  { code: "US", dialCode: "1", flag: "🇺🇸", name: "United States" },
  { code: "GB", dialCode: "44", flag: "🇬🇧", name: "United Kingdom" },
  { code: "JP", dialCode: "81", flag: "🇯🇵", name: "日本" },
  { code: "KR", dialCode: "82", flag: "🇰🇷", name: "한국" },
  { code: "HK", dialCode: "852", flag: "🇭🇰", name: "香港" },
  { code: "TW", dialCode: "886", flag: "🇹🇼", name: "台湾" },
  { code: "SG", dialCode: "65", flag: "🇸🇬", name: "Singapore" },
  { code: "AU", dialCode: "61", flag: "🇦🇺", name: "Australia" },
  { code: "DE", dialCode: "49", flag: "🇩🇪", name: "Deutschland" },
  { code: "FR", dialCode: "33", flag: "🇫🇷", name: "France" },
  { code: "CA", dialCode: "1", flag: "🇨🇦", name: "Canada" },
  { code: "IN", dialCode: "91", flag: "🇮🇳", name: "India" },
  { code: "BR", dialCode: "55", flag: "🇧🇷", name: "Brasil" },
  { code: "ES", dialCode: "34", flag: "🇪🇸", name: "España" },
  { code: "RU", dialCode: "7", flag: "🇷🇺", name: "Россия" },
  { code: "MY", dialCode: "60", flag: "🇲🇾", name: "Malaysia" },
  { code: "TH", dialCode: "66", flag: "🇹🇭", name: "ไทย" },
  { code: "VN", dialCode: "84", flag: "🇻🇳", name: "Việt Nam" },
  { code: "AE", dialCode: "971", flag: "🇦🇪", name: "الإمارات" },
];

function svgLock(): string {
  return `<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>`;
}
function escHtml(s: string): string {
  return (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function getApiBase(): string {
  // 跨站嵌入：用 SDK 源站，绝不回退 location.origin（宿主站会 404）
  return resolveApiBase();
}

/* ────── 组件状态（同一时刻仅一个面板，模块级状态足够） ────── */
let pvCountry: Country = COUNTRIES[0];
let pvCodeSent = false;
let pvTimer: ReturnType<typeof setInterval> | null = null;
let pvSec = 0;
let pvLocale: Locale = "zh-CN";

function vt(key: any, ...args: string[]): string { return t(key, pvLocale, ...args); }

export interface PvCtx {
  phone: string;
  dialCode: string;
  code: string;
  fullPhone: string;       // +<dial><phone>
  country: Country;
  root: ShadowRoot;
  setError: (msg: string) => void;
  setBusy: (busy: boolean) => void;
}

export interface PhoneVerifyOptions {
  locale: Locale;
  primaryColor: string;
  /** OTP 步骤提交按钮文案（如「开始语音对话」「请求回拨」） */
  submitLabel: string;
  /** 提交按钮前缀 SVG（可选） */
  submitIcon?: string;
  /** 是否在手机号步骤显示隐私一句话 */
  privacy?: boolean;
  /** 注入到手机号步骤的额外字段 HTML（如邮箱/留言） */
  extraFieldsHtml?: string;
  /** OTP 校验通过后回调，由宿主完成后续（拨号 / 提交线索 + 切视图） */
  onVerified: (ctx: PvCtx) => Promise<void> | void;
}

/* ────── HTML ────── */
export function phoneVerifyHtml(opts: PhoneVerifyOptions): string {
  pvLocale = opts.locale;
  const c = opts.primaryColor;
  // 记住上次国家
  try {
    const saved = storage.getCountryCode && storage.getCountryCode();
    const found = saved && COUNTRIES.find((x) => x.code === saved);
    if (found) pvCountry = found;
  } catch (e) { /* ignore */ }

  const opts6 = Array.from({ length: 6 }, (_, i) =>
    `<input type="text" maxlength="1" class="oc-pv-otp-cell" data-idx="${i}" inputmode="numeric" aria-label="${i + 1}" />`
  ).join("");

  const countryOptions = COUNTRIES.map((x) =>
    `<div class="oc-pv-country-opt" data-code="${x.code}" data-dial="${x.dialCode}">` +
      `<span class="oc-pv-flag">${x.flag}</span><span class="oc-pv-dial">+${x.dialCode}</span>` +
      `<span class="oc-pv-cname">${escHtml(x.name)}</span></div>`
  ).join("");

  return /* html */`
<div class="oc-pv" id="oc-pv-root">
  <ol class="oc-pv-steps" id="oc-pv-steps">
    <li class="is-active" data-s="1"><span class="oc-pv-step-n">1</span>${vt("voice.step1")}</li>
    <li data-s="2"><span class="oc-pv-step-n">2</span>${vt("voice.step2")}</li>
    <li data-s="3"><span class="oc-pv-step-n">3</span>${vt("voice.step3")}</li>
  </ol>

  <!-- 步骤 1：手机号 -->
  <div id="oc-pv-step-phone">
    <label class="oc-pv-label" for="oc-pv-phone">${vt("voice.fieldPhone")}<span class="oc-pv-req">${vt("callback.required")}</span></label>
    <div class="oc-pv-phone-row">
      <div class="oc-pv-country" id="oc-pv-country-trigger" role="button" tabindex="0" aria-haspopup="listbox">
        <span id="oc-pv-cur-flag">${pvCountry.flag}</span>
        <span id="oc-pv-cur-dial">+${pvCountry.dialCode}</span>
        <span class="oc-pv-caret">▾</span>
      </div>
      <input type="tel" id="oc-pv-phone" class="oc-pv-input" placeholder="${vt("voice.phonePlaceholder")}" maxlength="15" autocomplete="tel-national" />
    </div>
    <div class="oc-pv-country-dd" id="oc-pv-country-dd" style="display:none;" role="listbox">${countryOptions}</div>

    ${opts.extraFieldsHtml || ""}

    <button type="button" id="oc-pv-send" class="oc-pv-primary" style="background:${c};">${vt("voice.getCode")}</button>
    ${opts.privacy ? `<p class="oc-pv-privacy">${svgLock()} ${vt("voice.privacy")}</p>` : ""}
    <p id="oc-pv-err1" class="oc-pv-err" style="display:none;"></p>
  </div>

  <!-- 步骤 2：6 格 OTP -->
  <div id="oc-pv-step-otp" style="display:none;">
    <p class="oc-pv-otp-info" id="oc-pv-otp-info"></p>
    <div class="oc-pv-otp-row" id="oc-pv-otp-row">${opts6}</div>
    <button type="button" id="oc-pv-resend" class="oc-pv-resend">${vt("voice.resendCode")}</button>
    <button type="button" id="oc-pv-submit" class="oc-pv-primary" style="background:${c};">${opts.submitIcon || ""}<span>${escHtml(opts.submitLabel)}</span></button>
    <button type="button" id="oc-pv-back" class="oc-pv-back">${vt("pv.back")}</button>
    <p id="oc-pv-err2" class="oc-pv-err" style="display:none;"></p>
  </div>

  <style>${phoneVerifyStyles(c)}</style>
</div>`;
}

function phoneVerifyStyles(c: string): string {
  return `
.oc-pv-steps { display:flex;list-style:none;padding:0;margin:0 0 16px;gap:6px; }
.oc-pv-steps li { flex:1;display:flex;align-items:center;gap:5px;font-size:11px;color:var(--oc-text-secondary);line-height:1.2;opacity:0.55; }
.oc-pv-steps li.is-active { opacity:1;color:var(--oc-text);font-weight:600; }
.oc-pv-step-n { width:16px;height:16px;border-radius:50%;background:#cbd5e1;color:#fff;font-size:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0; }
.oc-pv-steps li.is-active .oc-pv-step-n { background:${c}; }
.oc-pv-label { display:block;font-size:12px;font-weight:600;color:var(--oc-text);margin:0 0 6px 2px; }
.oc-pv-req { font-size:11px;font-weight:500;color:#ef4444;margin-left:6px; }
.oc-pv-phone-row { display:flex;gap:8px;align-items:center;margin-bottom:12px;position:relative; }
.oc-pv-country { display:flex;align-items:center;gap:5px;padding:10px 10px;border:1px solid var(--oc-border);border-radius:8px;cursor:pointer;background:var(--oc-bg);font-size:13px;white-space:nowrap;color:var(--oc-text); }
.oc-pv-country:hover { border-color:${c}; }
.oc-pv-caret { font-size:10px;color:var(--oc-text-secondary); }
.oc-pv-input { flex:1;padding:10px 12px;border:1px solid var(--oc-border);border-radius:8px;font-size:14px;background:var(--oc-bg);color:var(--oc-text);outline:none; }
.oc-pv-input:focus { border-color:${c}; }
.oc-pv-country-dd { position:absolute;z-index:20;top:46px;left:0;width:100%;max-height:220px;overflow-y:auto;background:var(--oc-bg);border:1px solid var(--oc-border);border-radius:10px;box-shadow:0 8px 24px rgba(0,0,0,0.12); }
.oc-pv-country-opt { display:flex;align-items:center;gap:8px;padding:9px 12px;cursor:pointer;font-size:13px;color:var(--oc-text); }
.oc-pv-country-opt:hover { background:#f8f9ff; }
.oc-pv-flag { font-size:16px; }
.oc-pv-dial { min-width:42px; }
.oc-pv-cname { color:var(--oc-text-secondary);font-size:12px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap; }
.oc-pv-primary { width:100%;padding:13px;border:none;border-radius:12px;font-size:15px;font-weight:600;color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;transition:opacity 0.15s; }
.oc-pv-primary:hover:not(:disabled) { opacity:0.9; }
.oc-pv-primary:disabled { opacity:0.55;cursor:not-allowed; }
.oc-pv-privacy { font-size:11px;color:var(--oc-text-secondary);text-align:center;margin-top:10px;display:flex;align-items:center;justify-content:center;gap:4px;line-height:1.4; }
.oc-pv-privacy svg { flex-shrink:0; }
.oc-pv-err { font-size:13px;color:#ef4444;text-align:center;margin-top:8px; }
.oc-pv-otp-info { font-size:13px;color:var(--oc-text-secondary);margin:0 0 16px;line-height:1.5;text-align:center; }
.oc-pv-otp-row { display:flex;gap:8px;justify-content:center;margin-bottom:16px; }
.oc-pv-otp-cell { width:44px;height:52px;text-align:center;font-size:20px;font-weight:700;border:1px solid var(--oc-border);border-radius:8px;background:var(--oc-bg);color:var(--oc-text);outline:none; }
.oc-pv-otp-cell:focus { border-color:${c}; }
.oc-pv-resend { border:none;background:none;font-size:12px;color:${c};cursor:pointer;padding:0;margin-bottom:14px;display:block;width:100%;text-align:center; }
.oc-pv-resend:disabled { color:var(--oc-text-secondary);cursor:not-allowed; }
.oc-pv-back { width:100%;border:none;background:none;font-size:13px;color:var(--oc-text-secondary);cursor:pointer;padding:10px 0 0; }
`;
}

/* ────── 事件绑定 ────── */
export function bindPhoneVerify(opts: PhoneVerifyOptions): void {
  pvLocale = opts.locale;
  pvCodeSent = false;
  stopPvCountdown();
  const root = getShadowRoot();
  if (!root) return;

  const $ = (id: string) => root.getElementById(id);
  const setError = (sel: string, msg: string) => {
    const el = $(sel);
    if (el) { el.style.display = msg ? "block" : "none"; el.textContent = msg; }
  };

  /* 国家选择器 */
  const trigger = $("oc-pv-country-trigger");
  const dd = $("oc-pv-country-dd");
  trigger?.addEventListener("click", () => {
    if (dd) dd.style.display = dd.style.display === "none" ? "block" : "none";
  });
  dd?.addEventListener("click", (e) => {
    const opt = (e.target as HTMLElement).closest(".oc-pv-country-opt") as HTMLElement;
    if (!opt) return;
    const code = opt.getAttribute("data-code") || "";
    const found = COUNTRIES.find((x) => x.code === code);
    if (found) {
      pvCountry = found;
      try { storage.setCountryCode && storage.setCountryCode(found.code); } catch (e) { /* ignore */ }
      const f = $("oc-pv-cur-flag"); if (f) f.textContent = found.flag;
      const d = $("oc-pv-cur-dial"); if (d) d.textContent = "+" + found.dialCode;
    }
    if (dd) dd.style.display = "none";
  });
  root.addEventListener("click", (e) => {
    if (!trigger?.contains(e.target as Node) && !dd?.contains(e.target as Node)) {
      if (dd) dd.style.display = "none";
    }
  });

  /* 发码 → 进入 OTP 步骤 */
  const sendBtn = $("oc-pv-send") as HTMLButtonElement | null;
  sendBtn?.addEventListener("click", async () => {
    const phone = (($("oc-pv-phone") as HTMLInputElement)?.value || "").trim();
    if (!phone) { setError("oc-pv-err1", vt("voice.needPhone")); return; }
    setError("oc-pv-err1", "");
    sendBtn.disabled = true;
    const prev = sendBtn.textContent;
    sendBtn.textContent = vt("callback.sending");
    const ok = await sendCode(phone, (m) => setError("oc-pv-err1", m));
    sendBtn.disabled = false;
    sendBtn.textContent = prev;
    if (!ok) return;
    pvCodeSent = true;
    // 切到 OTP 步骤
    const sp = $("oc-pv-step-phone"); if (sp) sp.style.display = "none";
    const so = $("oc-pv-step-otp"); if (so) so.style.display = "block";
    markStep(root, 2);
    const info = $("oc-pv-otp-info");
    if (info) info.textContent = vt("callback.verifyInfo", "+" + pvCountry.dialCode, phone.slice(-4));
    startPvCountdown(root);
    const first = root.querySelector(".oc-pv-otp-cell") as HTMLInputElement;
    first?.focus();
  });

  /* OTP 输入：自动跳格 / 退格 / 粘贴 */
  const cells = Array.from(root.querySelectorAll(".oc-pv-otp-cell")) as HTMLInputElement[];
  cells.forEach((cell, i) => {
    cell.addEventListener("input", () => {
      cell.value = cell.value.replace(/\D/g, "").slice(0, 1);
      if (cell.value && i < cells.length - 1) cells[i + 1].focus();
    });
    cell.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Backspace" && !cell.value && i > 0) cells[i - 1].focus();
    });
    cell.addEventListener("paste", (e: ClipboardEvent) => {
      e.preventDefault();
      const txt = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
      for (let k = 0; k < txt.length && k < cells.length; k++) cells[k].value = txt[k];
      cells[Math.min(txt.length, cells.length - 1)].focus();
    });
  });

  /* 重新发送 */
  const resend = $("oc-pv-resend") as HTMLButtonElement | null;
  resend?.addEventListener("click", async () => {
    if (resend.disabled) return;
    const phone = (($("oc-pv-phone") as HTMLInputElement)?.value || "").trim();
    const ok = await sendCode(phone, (m) => setError("oc-pv-err2", m));
    if (ok) startPvCountdown(root);
  });

  /* 返回手机号步骤 */
  $("oc-pv-back")?.addEventListener("click", () => {
    const so = $("oc-pv-step-otp"); if (so) so.style.display = "none";
    const sp = $("oc-pv-step-phone"); if (sp) sp.style.display = "block";
    markStep(root, 1);
    setError("oc-pv-err2", "");
  });

  /* 提交：校验 OTP → onVerified */
  const submit = $("oc-pv-submit") as HTMLButtonElement | null;
  submit?.addEventListener("click", async () => {
    const code = cells.map((c) => c.value).join("");
    if (code.length < 6) { setError("oc-pv-err2", vt("callback.needFullCode")); return; }
    setError("oc-pv-err2", "");
    const setBusy = (b: boolean) => { if (submit) submit.disabled = b; };
    setBusy(true);
    const phone = (($("oc-pv-phone") as HTMLInputElement)?.value || "").trim();
    const fullPhone = "+" + pvCountry.dialCode + phone;
    try {
      const apiBase = getApiBase();
      const res = await fetch(`${apiBase}/auth/verify-sms-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ phone: fullPhone, code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.valid) throw new Error(data.error || vt("voice.codeWrong"));
      // 校验通过 → 交给宿主
      await opts.onVerified({
        phone, dialCode: pvCountry.dialCode, code, fullPhone, country: pvCountry, root,
        setError: (m) => setError("oc-pv-err2", m),
        setBusy,
      });
    } catch (e: any) {
      setError("oc-pv-err2", e?.message || vt("voice.codeWrongRetry"));
      setBusy(false);
    }
  });
}

async function sendCode(phone: string, onErr: (m: string) => void): Promise<boolean> {
  if (!phone) { onErr(vt("voice.needPhone")); return false; }
  try {
    const apiBase = getApiBase();
    const res = await fetch(`${apiBase}/auth/send-sms-code`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({ phone: "+" + pvCountry.dialCode + phone }),
    });
    if (!res.ok) {
      let m = ""; try { const b = await res.json(); m = b?.error || ""; } catch { /* ignore */ }
      throw new Error(m || vt("voice.sendFail"));
    }
    return true;
  } catch (e: any) {
    onErr(e?.message || vt("voice.sendFail"));
    return false;
  }
}

function markStep(root: ShadowRoot, step: number): void {
  const items = root.querySelectorAll("#oc-pv-steps li");
  items.forEach((li) => {
    const s = Number((li as HTMLElement).getAttribute("data-s"));
    li.classList.toggle("is-active", s <= step);
  });
}

function startPvCountdown(root: ShadowRoot): void {
  stopPvCountdown();
  pvSec = 60;
  const btn = root.getElementById("oc-pv-resend") as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = vt("callback.resendIn", String(pvSec)); }
  pvTimer = setInterval(() => {
    pvSec--;
    if (btn) btn.textContent = vt("callback.resendIn", String(pvSec));
    if (pvSec <= 0) {
      stopPvCountdown();
      if (btn) { btn.disabled = false; btn.textContent = vt("voice.resendCode"); }
    }
  }, 1000);
}

export function stopPvCountdown(): void {
  if (pvTimer) { clearInterval(pvTimer); pvTimer = null; }
  pvSec = 0;
}
